import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Users, MonitorPlay, Award } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { courses } from "@/data/courses";
import { usePageMeta } from "@/lib/api";

export default function AcademyPage() {
  usePageMeta(
    "SarvaRitam ESG & Sustainability Academy — Corporate Training",
    "ESG training programmes: fundamentals, BRSR, GHG accounting, climate risk, sustainable finance, responsible investment, boards and more."
  );
  return (
    <>
      <PageHero
        eyebrow="SarvaRitam ESG Academy"
        title={[<>ESG capability, <span className="serif-it text-gold">built internally.</span></>]}
        intro="Training that goes beyond awareness: programmes built around your sector, your data and your people — from board briefings to hands-on technical workshops."
      />
      <section className="bg-offwhite text-charcoal sec" data-testid="academy-page">
        <div className="wrap">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                <article className="lift flex flex-col h-full border rule-light bg-white/60 hover:bg-white hover:border-forest p-8 rounded-sm" data-testid={`course-card-${c.slug}`}>
                  <h2 className="font-display font-semibold text-xl leading-snug">{c.title}</h2>
                  <div className="grid grid-cols-1 gap-2 mt-5 text-[12.5px] text-charcoal/65">
                    <p className="flex items-center gap-2.5"><Clock size={13} className="text-gold shrink-0" /> {c.duration}</p>
                    <p className="flex items-center gap-2.5"><Users size={13} className="text-gold shrink-0" /> {c.audience}</p>
                    <p className="flex items-center gap-2.5"><MonitorPlay size={13} className="text-gold shrink-0" /> {c.format}</p>
                  </div>
                  <p className="eyebrow text-forest mt-6 mb-3">Learning outcomes</p>
                  <ul className="space-y-2 flex-1">
                    {c.outcomes.map((o) => (
                      <li key={o} className="flex items-start gap-2.5 text-[13px] text-charcoal/70">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" /> {o}
                      </li>
                    ))}
                  </ul>
                  <p className="flex items-start gap-2.5 text-[11px] text-charcoal/45 mt-6 pt-5 border-t rule-light">
                    <Award size={13} className="text-sage shrink-0 mt-0.5" /> {c.certificate}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 bg-forest text-offwhite p-10 sm:p-14 relative grain overflow-hidden" data-testid="academy-corporate">
              <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(50% 70% at 85% 20%, rgba(198,161,91,0.5), transparent 65%)" }} />
              <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <p className="eyebrow text-gold mb-4">Custom corporate training</p>
                  <h2 className="h-display font-semibold text-2xl sm:text-3xl lg:text-4xl leading-tight">
                    Every programme can be tailored — sector, seniority, duration and data.
                  </h2>
                  <p className="text-white/85 text-[15px] mt-5 leading-relaxed max-w-2xl">
                    Corporate programmes are designed around your organisation's material issues and reporting obligations,
                    using your own examples where appropriate. Tell us who needs to learn what, and we'll design it.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link to="/contact?service=Training" className="btn-gold px-8 py-4" data-testid="academy-corporate-cta">
                    Request Corporate Training <ArrowUpRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
